import type { EventBooking } from '@/types/events';

export interface ProducerBookingGroup {
  key: string;
  personName: string | null;
  personEmail: string;
  userId: string | null;
  status: string;
  totalSpots: number;
  latestBookedAt: string;
  items: EventBooking[];
}

/**
 * @deprecated Use booking.booking_code directly from DB instead.
 * Kept only as fallback for legacy data without booking_code column.
 */
export function formatBookingCode(token: string): string {
  return token.replace(/-/g, '').slice(0, 8).toUpperCase();
}

/**
 * Returns the authoritative booking code from the DB field,
 * falling back to legacy derivation only if booking_code is missing.
 */
export function getBookingCode(booking: { booking_code?: string; confirmation_token: string }): string {
  if (booking.booking_code) return booking.booking_code;
  return formatBookingCode(booking.confirmation_token);
}

/**
 * Groups producer-facing bookings by person + occurrence + status.
 *
 * Group key: event_id :: occurrence_id|start_date :: booker_identity :: status
 *
 * Returns groups sorted by latestBookedAt desc (most recent first).
 * Items within each group sorted by created_at desc.
 */
export function groupProducerBookings(bookings: EventBooking[]): ProducerBookingGroup[] {
  const map = new Map<string, ProducerBookingGroup>();

  for (const b of bookings) {
    // Occurrence key
    const occKey = b.occurrence_id ?? b.event_id;

    // Booker identity: user_id if present, otherwise email lowercased
    const bookerKey = b.user_id
      ? `uid:${b.user_id}`
      : `email:${(b.guest_email ?? '').trim().toLowerCase()}`;

    const key = `${b.event_id}::${occKey}::${bookerKey}::${b.status}`;

    const existing = map.get(key);
    if (existing) {
      existing.totalSpots += b.spots;
      existing.items.push(b);
      // Track latest booked at
      if (b.created_at > existing.latestBookedAt) {
        existing.latestBookedAt = b.created_at;
      }
      // Prefer non-null name
      if (!existing.personName && b.guest_name) {
        existing.personName = b.guest_name;
      }
    } else {
      map.set(key, {
        key,
        personName: b.guest_name,
        personEmail: b.guest_email,
        userId: b.user_id,
        status: b.status,
        totalSpots: b.spots,
        latestBookedAt: b.created_at,
        items: [b],
      });
    }
  }

  const groups = Array.from(map.values());

  // Sort items within each group by created_at desc
  for (const g of groups) {
    g.items.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  // Sort groups by latestBookedAt desc
  groups.sort((a, b) => b.latestBookedAt.localeCompare(a.latestBookedAt));

  return groups;
}

/**
 * Filter groups by search query matching name, email, or booking code.
 * Returns groups whose items match. If a match is found inside a multi-item group,
 * the whole group is returned.
 */
export function filterBookingGroups(
  groups: ProducerBookingGroup[],
  query: string
): ProducerBookingGroup[] {
  if (!query.trim()) return groups;

  const q = query.trim().toLowerCase();

  return groups.filter((g) => {
    if (g.personName?.toLowerCase().includes(q)) return true;
    if (g.personEmail.toLowerCase().includes(q)) return true;
    return g.items.some((item) =>
      getBookingCode(item).toLowerCase().includes(q)
    );
  });
}
